import React from 'react';
import { toNumber } from 'lodash';
import { Text, View, Table, Field, Card, Page } from '../../components';
import { Theme } from '../../../../theme';
import {
  formatCurrencyOnBlur,
  limitString,
  paginateArray,
  parseDateValue,
} from '../../../../helpers/utils';

interface Props {
  data: any[];
  meta: any;
}

const CurrentAwards = ({ data: projects, meta }: Props) => {
  const initialData = projects.slice(0, 4);
  const remainingData = paginateArray(projects.slice(4), 11);

  const aggregates = projects.reduce(
    (acc, project) => {
      acc.total += 1;
      acc.amount += toNumber(project.amount_awarded);
      acc.hours += toNumber(project.actual_hours);
      return acc;
    },
    {
      total: 0,
      amount: 0,
      hours: 0,
    },
  );

  const columns = [
    {
      Header: 'Client',
      accessor: 'client_name',
    },
    {
      Header: 'Funder',
      accessor: 'funder_name',
      Cell: ({ cell: { value } }) => value || '--',
    },
    {
      Header: 'Project',
      accessor: 'description',
      width: 200,
      Cell: ({ cell: { value } }) => <Text>{limitString(value, 200)}</Text>,
    },
    {
      Header: 'Amount Requested',
      accessor: 'amount_requested',
      Cell: ({
        row: {
          original: { amount_requested },
        },
      }) => {
        const amount = toNumber(amount_requested);
        return (
          <Text>{amount ? formatCurrencyOnBlur(amount.toString()) : '--'}</Text>
        );
      },
    },
    {
      Header: 'Amount Awarded',
      accessor: 'amount',
      Cell: ({
        row: {
          original: { amount_awarded },
        },
      }) => {
        const amount = toNumber(amount_awarded);
        return (
          <Text>{amount ? formatCurrencyOnBlur(amount.toString()) : '--'}</Text>
        );
      },
    },
    {
      Header: 'Start Date',
      accessor: 'award_start',
      Cell: ({ cell: { value } }) =>
        value ? parseDateValue(value).format('YYYY-MM-DD') : '--',
    },
    {
      Header: 'End Date',
      accessor: 'award_end',
      Cell: ({ cell: { value } }) =>
        value ? parseDateValue(value).format('YYYY-MM-DD') : '--',
    },
  ];

  const clients = [
    ...new Set(projects.map((project) => project.client_name)),
  ].join('; ');

  return (
    <>
      <Page meta={meta}>
        <View>
          <Card mb="20pt">
            <View
              pb={1}
              mb={3}
              borderBottom={`1pt solid ${Theme.colors.primary}`}
              width="100%"
            >
              <Text fontSize="14pt">Aggregates</Text>
            </View>
            <Field label="Clients" value={clients} />
            <Field label="Total Projects" value={aggregates.total} />
            <Field
              label="Total Awarded Amount"
              value={formatCurrencyOnBlur(aggregates.amount.toString())}
            />
            <Field
              label="Total Actual Hours"
              value={aggregates.hours.toFixed(2)}
            />
          </Card>
          <View
            pb={1}
            mb={3}
            borderBottom={`1pt solid ${Theme.colors.primary}`}
            width="100%"
          >
            <Text fontSize="14pt">Projects</Text>
          </View>
          <Table data={initialData} columns={columns} />
        </View>
      </Page>
      {remainingData.map((data, i) => (
        <Page meta={meta} key={`page_${i + 1}`}>
          <Table data={data} columns={columns} />
        </Page>
      ))}
    </>
  );
};

export default CurrentAwards;
